// LoadCalcPopover — Manual J quick-reference for HVAC techs.
// NOT a full load calc (that requires 100+ inputs and ACCA software).
// This is the "rule of thumb + climate correction" cheat sheet techs use
// to sanity-check a replacement job in the truck before ordering equipment.
//
// Ranges here are typical residential replacement guidance. Commercial and
// unusual jobs should still get a proper Manual J.
import { useState } from "react";
import { Calculator, X, Info } from "@phosphor-icons/react";

// Climate zone → BTU/hr per sqft for cooling (rule of thumb, replacement sizing)
// Sourced from ACCA Manual J typical envelopes for existing homes.
const CLIMATE_ZONES = [
  { key: "hot_humid",    label: "1 · Hot & Humid (Miami, Houston)",    cool_low: 30, cool_high: 35, heat_low: 20, heat_high: 30 },
  { key: "hot_dry",      label: "2 · Hot & Dry (Phoenix, Vegas)",      cool_low: 25, cool_high: 32, heat_low: 20, heat_high: 30 },
  { key: "mixed_humid",  label: "3 · Mixed Humid (Atlanta, Charlotte)", cool_low: 22, cool_high: 28, heat_low: 30, heat_high: 40 },
  { key: "mixed",        label: "4 · Mixed (Philly, DC, KC)",           cool_low: 20, cool_high: 25, heat_low: 35, heat_high: 45 },
  { key: "cold",         label: "5 · Cold (Chicago, Boston, Detroit)",  cool_low: 18, cool_high: 22, heat_low: 40, heat_high: 55 },
  { key: "very_cold",    label: "6 · Very Cold (Minneapolis, Fargo)",   cool_low: 15, cool_high: 20, heat_low: 50, heat_high: 65 },
  { key: "subarctic",    label: "7-8 · Subarctic (AK)",                 cool_low: 12, cool_high: 18, heat_low: 60, heat_high: 80 },
];

const INSULATION = {
  poor:    { label: "Poor · Pre-1980, single-pane",       mult: 1.20 },
  average: { label: "Average · 1980-2005, double-pane",    mult: 1.00 },
  good:    { label: "Good · Post-2005, tight envelope",    mult: 0.85 },
  excellent: { label: "Excellent · Passive/Retrofit tight", mult: 0.70 },
};

const SUN_EXPOSURE = {
  shaded:    { label: "Mostly Shaded",   mult: 0.92 },
  average:   { label: "Average",         mult: 1.00 },
  full_sun:  { label: "Full Sun / West-facing", mult: 1.10 },
};

const CEILING = {
  eight:    { label: `8' Standard`, mult: 1.00 },
  nine:     { label: `9' Ceiling`,  mult: 1.10 },
  ten_plus: { label: `10'+ / Vaulted`, mult: 1.20 },
};

// Duct sizing quick reference for supply trunks
const DUCT_QUICKREF = [
  { cfm: 400,  round: '10"', rect: "8x14"  },
  { cfm: 600,  round: '12"', rect: "8x18"  },
  { cfm: 800,  round: '14"', rect: "10x18" },
  { cfm: 1000, round: '14"', rect: "10x20" },
  { cfm: 1200, round: '16"', rect: "12x20" },
  { cfm: 1600, round: '18"', rect: "12x24" },
  { cfm: 2000, round: '20"', rect: "14x26" },
];

const TON_SIZES = [1.5, 2, 2.5, 3, 3.5, 4, 5];
const BTU_PER_TON = 12000;
const CFM_PER_TON = 400;

// Round BTU up to the nearest standard tonnage. Manual J best-practice is
// to prefer the size below when between two — oversizing kills humidity
// control and short-cycles the compressor.
const pickTonnage = (btu) => {
  const tons = btu / BTU_PER_TON;
  // Return closest standard size, preferring lower to prevent oversizing
  const lower = TON_SIZES.filter(t => t <= tons + 0.15).slice(-1)[0] || TON_SIZES[0];
  const upper = TON_SIZES.find(t => t > lower) || lower;
  return { lower, upper, exact: tons };
};

export default function LoadCalcPopover({ onClose }) {
  const [sqft, setSqft] = useState("");
  const [zone, setZone] = useState("mixed");
  const [insul, setInsul] = useState("average");
  const [sun, setSun] = useState("average");
  const [ceiling, setCeiling] = useState("eight");

  const cz = CLIMATE_ZONES.find(c => c.key === zone) || CLIMATE_ZONES[3];
  const mult = INSULATION[insul].mult * SUN_EXPOSURE[sun].mult * CEILING[ceiling].mult;

  const sf = parseFloat(sqft) || 0;
  const coolLow = Math.round(sf * cz.cool_low * mult);
  const coolHigh = Math.round(sf * cz.cool_high * mult);
  const heatLow = Math.round(sf * cz.heat_low * mult);
  const heatHigh = Math.round(sf * cz.heat_high * mult);
  const coolTons = sf ? pickTonnage((coolLow + coolHigh) / 2) : null;
  const cfm = coolTons ? Math.round((coolTons.lower + coolTons.upper) / 2 * CFM_PER_TON) : null;

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="load-calc-modal">
      <div onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Calculator weight="fill" size={22} className="text-cyan-500"/>
            <div>
              <div className="font-display font-black text-xl">Load Calc Quick Ref</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Manual J · Replacement Sizing</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800" data-testid="load-calc-close">
            <X size={20}/>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="p-3 rounded-lg bg-amber-50 border-l-4 border-amber-500 text-amber-900 text-xs flex gap-2">
            <Info weight="fill" size={16} className="flex-shrink-0 mt-0.5"/>
            <span><b>For sanity checks only.</b> Any new install, addition, or unusual envelope still needs a proper Manual J (ACCA rules of thumb ≠ code compliance). Never oversize AC — humidity + short cycling kills equipment life.</span>
          </div>

          {/* Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Conditioned Sq Ft">
              <input type="number" value={sqft} onChange={e => setSqft(e.target.value)}
                placeholder="e.g. 1800" data-testid="load-calc-sqft" className={inputCls}/>
            </Field>
            <Field label="Climate Zone">
              <select value={zone} onChange={e => setZone(e.target.value)}
                data-testid="load-calc-zone" className={inputCls}>
                {CLIMATE_ZONES.map(z => <option key={z.key} value={z.key}>{z.label}</option>)}
              </select>
            </Field>
            <Field label="Insulation / Age">
              <select value={insul} onChange={e => setInsul(e.target.value)}
                data-testid="load-calc-insul" className={inputCls}>
                {Object.entries(INSULATION).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Sun Exposure">
              <select value={sun} onChange={e => setSun(e.target.value)}
                data-testid="load-calc-sun" className={inputCls}>
                {Object.entries(SUN_EXPOSURE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Ceiling Height">
              <select value={ceiling} onChange={e => setCeiling(e.target.value)}
                data-testid="load-calc-ceiling" className={inputCls}>
                {Object.entries(CEILING).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
          </div>

          {/* Results */}
          {sf > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="load-calc-result">
              <div className="p-4 rounded-xl bg-cyan-500 text-white">
                <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Cooling Load</div>
                <div className="text-2xl font-display font-black">
                  {coolLow.toLocaleString()} – {coolHigh.toLocaleString()} BTU/hr
                </div>
                {coolTons && (
                  <div className="text-sm font-bold mt-1">
                    Size: <span className="text-yellow-200">{coolTons.lower}-{coolTons.upper} tons</span>
                  </div>
                )}
                {cfm && (
                  <div className="text-[11px] opacity-90 mt-1">Airflow target: {cfm} CFM ({(cfm/coolTons.lower).toFixed(0)} CFM/ton)</div>
                )}
              </div>
              <div className="p-4 rounded-xl bg-orange-500 text-white">
                <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Heating Load</div>
                <div className="text-2xl font-display font-black">
                  {heatLow.toLocaleString()} – {heatHigh.toLocaleString()} BTU/hr
                </div>
                <div className="text-[11px] opacity-90 mt-1">
                  Furnace: pick output rated ≥ {Math.round(heatHigh / 1000) * 1000} BTU
                </div>
              </div>
            </div>
          )}

          {/* Sizing rules of thumb */}
          <div>
            <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Sizing Rules of Thumb</div>
            <ul className="text-xs text-slate-700 space-y-1 list-disc pl-5">
              <li><b>Never round up on AC.</b> If between 3 and 3.5 tons, take the 3. Short-cycling wrecks compressors and humidity.</li>
              <li><b>Round up on furnaces.</b> Gas furnaces are output-rated — undersizing = cold house at design temp.</li>
              <li><b>400 CFM per ton of cooling</b> is the design airflow target. Verify duct static.</li>
              <li><b>1 ton = 12,000 BTU/hr</b>. Heat pump heating capacity drops sharply below 30°F — need aux strip.</li>
              <li><b>Sensible-Total ratio ~0.75</b> in humid climates — right-size for latent (moisture) removal.</li>
            </ul>
          </div>

          {/* Duct sizing quick ref */}
          <div>
            <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Supply Trunk Sizing</div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-600 px-3 py-2">
                <div>Design CFM</div><div>Round Duct</div><div>Rectangular</div>
              </div>
              {DUCT_QUICKREF.map((d, i) => {
                const isCurrent = cfm && d.cfm >= cfm - 100 && d.cfm <= cfm + 200;
                return (
                  <div key={d.cfm}
                    className={`grid grid-cols-3 px-3 py-1.5 text-xs border-b border-slate-100 ${isCurrent ? "bg-cyan-50 font-bold" : ""}`}>
                    <div className="font-mono">{d.cfm}</div>
                    <div className="font-mono">{d.round}</div>
                    <div className="font-mono">{d.rect}</div>
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              Sizes assume 0.1" WC/100ft friction rate. Verify with duct calculator on unusual runs.
            </div>
          </div>

          {/* Common mistakes */}
          <div>
            <div className="text-[10px] uppercase font-black tracking-widest text-red-700 mb-2">Common Sizing Mistakes</div>
            <ul className="text-xs text-red-900 space-y-1 list-disc pl-5">
              <li><b>Oversized AC in humid climate</b> — never runs long enough to pull moisture. Homeowner complaints: "clammy but cold."</li>
              <li><b>Furnace matches old size without envelope check</b> — new windows/insulation cuts load 20-30%.</li>
              <li><b>Ignoring duct static</b> — a 3-ton coil on undersized ducts moves 2 tons of air.</li>
              <li><b>Skipping load calc on additions</b> — new sunroom or garage conversion changes everything.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium";
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
